import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, X, ArrowRight, ShieldAlert } from 'lucide-react';
import { Button } from './ui/Button';
import {
  closePlanLimitModal,
  subscribePlanLimitModal,
  updatePlanLimitUsage,
  type PlanLimitInfo,
} from '../services/planLimitModal';
import { homeApi } from '../services/homeApi';
import { useActiveHomeUnitId } from '../hooks/useActiveHomeUnitId';

/**
 * Global upgrade-suggest modal (2026-08-31, Jaime feature): shown whenever any
 * API call is rejected with a plan-limit 403 (code 'PLAN_LIMIT'). Mounted once
 * in App.tsx; opened from the shared axios response interceptor so EVERY page
 * (personal, residentes, mascotas, vehículos, calendario, …) gets the popup
 * without per-page wiring.
 */
// Fallback fetchers: when the 403 payload lacks `current_usage` (home-limits
// enforcement does; billing checkCapacity does), count via the module list API.
const USAGE_FETCH: Record<string, (unitId?: string) => Promise<number>> = {
  vehicles: async (unitId) => {
    const r = await homeApi.vehicles.list(unitId);
    const v = r.data?.data?.vehicles;
    return Array.isArray(v) ? v.length : 0;
  },
  pets: async (unitId) => {
    const r = await homeApi.pets.list(unitId);
    const v = r.data?.data?.pets;
    return Array.isArray(v) ? v.length : 0;
  },
  shopping: async (unitId) => {
    const r = await homeApi.shopping.list(unitId);
    const v = r.data?.data?.items;
    return Array.isArray(v) ? v.length : 0;
  },
  tasks: async (unitId) => {
    const r = await homeApi.tasks.list(unitId);
    const v = r.data?.data?.tasks;
    return Array.isArray(v) ? v.length : 0;
  },
  notes: async (unitId) => {
    const r = await homeApi.notes.list(unitId);
    const n = r.data?.data?.notes ?? r.data?.notes;
    return Array.isArray(n) ? n.length : 0;
  },
};

export function PlanLimitModal() {
  const navigate = useNavigate();
  const [info, setInfo] = useState<PlanLimitInfo | null>(null);
  // FIX LANE #166 (2026-09-24): home-module list APIs require a unitId
  // (backend `requireUnit` fails closed with 400 when ambiguous). Pass the
  // active unit; when none is selected, skip the usage fetch and leave "—".
  const activeUnitId = useActiveHomeUnitId();

  useEffect(() => subscribePlanLimitModal(setInfo), []);

  // FIX (2026-09-02, Jaime bug): home-module 403s carry current_limit but no
  // current_usage, so the modal showed "Uso actual: —/1". Fetch the real count.
  useEffect(() => {
    if (!info || typeof info.usage === 'number' || typeof info.limit !== 'number' || !info.module) return;
    const fetcher = USAGE_FETCH[info.module];
    if (!fetcher || !activeUnitId) return;
    let cancelled = false;
    fetcher(activeUnitId)
      .then((usage) => { if (!cancelled) updatePlanLimitUsage(info.module as string, usage); })
      .catch(() => { /* leave the dash if the count fetch fails */ });
    return () => { cancelled = true; };
  }, [info, activeUnitId]);

  if (!info) return null;

  const handleUpgrade = () => {
    closePlanLimitModal();
    navigate('/pricing');
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
      onClick={closePlanLimitModal}
    >
      <div
        className="relative bg-[var(--v-surface-1)] rounded-[var(--v-radius-xl)] shadow-xl max-w-md w-full p-8 text-center border border-[var(--v-border-light)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={closePlanLimitModal}
          aria-label="Cerrar"
          className="absolute top-4 right-4 text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="w-16 h-16 rounded-full bg-[var(--v-teal-50)] flex items-center justify-center mx-auto mb-4">
          <Crown className="w-8 h-8 text-[var(--v-teal-700)]" />
        </div>
        <h2 className="text-xl font-bold text-[var(--v-text-primary)] mb-2">
          Límite del plan alcanzado
        </h2>
        <p className="text-sm text-[var(--v-text-secondary)] mb-4">{info.message}</p>
        {typeof info.limit === 'number' && (
          <div className="flex items-center justify-center gap-2 mb-6 text-xs text-[var(--v-text-secondary)]">
            <ShieldAlert className="w-4 h-4" />
            <span>
              Uso actual: {info.usage ?? '—'} / {info.limit}
              {info.module ? ` · módulo: ${info.module}` : ''}
            </span>
          </div>
        )}
        <Button variant="primary" size="lg" fullWidth onClick={handleUpgrade}>
          Ver planes y mejorar
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
        <button
          onClick={closePlanLimitModal}
          className="mt-3 text-sm text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]"
        >
          Ahora no
        </button>
      </div>
    </div>
  );
}
